import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MessageAttachment, TranscriptItem } from 'fe-be-utils';
import { INTERACTIVE_TYPE_TO_TOOL_NAME } from 'fe-be-utils';
import type { GetWidgetChatResponse, SendWidgetMessageResponse } from 'server';
import { appFetch } from './appFetch';
import { widgetKeys } from './keys';

type SendMessageParams = {
  message: string;
  userToken: string | null;
  files?: File[];
};

export const useSendMessage = (chatId: string, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SendMessageParams): Promise<SendWidgetMessageResponse> => {
      if (params.files && params.files.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('organizationId', organizationId);
        formData.append('chatId', chatId);
        formData.append('message', params.message);
        if (params.userToken) {
          formData.append('userToken', params.userToken);
        }
        for (const file of params.files) {
          formData.append('files', file);
        }
        const response = await appFetch<SendWidgetMessageResponse>(
          `/app/public/widget/chats/${chatId}/message`,
          {
            method: 'POST',
            body: formData as unknown as NonNullable<object>
          }
        );
        return response;
      }

      // JSON path for text-only messages
      const response = await appFetch<SendWidgetMessageResponse>(
        `/app/public/widget/chats/${chatId}/message`,
        {
          method: 'POST',
          body: {
            organizationId,
            chatId,
            message: params.message,
            userToken: params.userToken
          }
        }
      );
      return response;
    },
    onMutate: async (params: SendMessageParams) => {
      await queryClient.cancelQueries({ queryKey: widgetKeys.chat(chatId) });

      // Snapshot the previous value
      const previousChat = queryClient.getQueryData<GetWidgetChatResponse>(widgetKeys.chat(chatId));

      // Build optimistic attachments from selected files
      const optimisticAttachments: MessageAttachment[] | undefined =
        params.files && params.files.length > 0
          ? params.files.map((file): MessageAttachment => {
              const url = URL.createObjectURL(file);
              if (file.type.startsWith('image/')) {
                return { type: 'image', url, mimeType: file.type };
              }
              return { type: 'file', url, fileName: file.name, mimeType: file.type };
            })
          : undefined;

      // Optimistically update by adding the user's message
      if (previousChat?.chat) {
        queryClient.setQueryData<GetWidgetChatResponse>(widgetKeys.chat(chatId), {
          chat: {
            ...previousChat.chat,
            transcript: {
              ...previousChat.chat.transcript,
              messages: [
                ...(previousChat.chat.transcript?.messages || []),
                {
                  role: 'user',
                  content: params.message,
                  createdAt: new Date().toISOString(),
                  ...(optimisticAttachments && { attachments: optimisticAttachments })
                }
              ]
            }
          }
        });
      }

      // Return a context object with the snapshot
      return { previousChat };
    },
    onError: (_error, _params, context) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousChat) {
        queryClient.setQueryData(widgetKeys.chat(chatId), context.previousChat);
      }
    },
    onSuccess: (data) => {
      // Directly update the cache with the assistant's response
      queryClient.setQueryData<GetWidgetChatResponse>(
        widgetKeys.chat(chatId),
        (old: GetWidgetChatResponse | undefined): GetWidgetChatResponse | undefined => {
          if (!old?.chat) return old;
          const currentMessages = old.chat.transcript?.messages || [];
          const newMessages: TranscriptItem[] = [...currentMessages];

          if (data.interactiveComponent) {
            // Add as a ToolCallMessage so the component state persists in the transcript
            const toolName = INTERACTIVE_TYPE_TO_TOOL_NAME[data.interactiveComponent.type];
            newMessages.push({
              role: 'tool',
              toolCallId: `ic-${Date.now()}`,
              toolName,
              toolArguments: JSON.stringify(data.interactiveComponent.params),
              createdAt: new Date().toISOString()
            });
          } else if (data.message) {
            newMessages.push({
              role: 'bot',
              content: data.message,
              createdAt: new Date().toISOString()
            });
          }

          return {
            chat: {
              ...old.chat,
              transcript: {
                ...old.chat.transcript,
                messages: newMessages
              }
            }
          };
        }
      );
    }
  });
};
